import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useOrgBatchManagement } from '@/lib/hooks/useOrg';

interface Props {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (v: boolean) => void;
  batchId?: string;
  initial?: { name: string; start_date?: string; end_date?: string };
  onSaved?: (id: string) => void;
}

const field = 'h-10 w-full rounded-xl border border-[#ececec] bg-white px-3 text-sm text-[#111827] focus:border-[#ff5a1f] focus:outline-none';

export function BatchFormDialog({ mode, open, onOpenChange, batchId, initial, onSaved }: Props) {
  const m = useOrgBatchManagement(batchId);
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setStart(initial?.start_date ?? '');
      setEnd(initial?.end_date ?? '');
    }
  }, [open, initial]);

  const pending = m.createBatch.isPending || m.updateBatch.isPending;
  const canSave = name.trim().length > 0 && !pending;

  const save = () => {
    const body = { name: name.trim(), start_date: start, end_date: end };
    if (mode === 'create') {
      m.createBatch.mutate(body, { onSuccess: (b) => { onSaved?.(b.id); onOpenChange(false); } });
    } else if (batchId) {
      m.updateBatch.mutate({ batchId, body }, { onSuccess: () => onOpenChange(false) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New batch' : 'Edit batch'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[#374151]">Batch name</span>
            <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. DSA — Winter 2026" autoFocus />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[#374151]">Start date</span>
              <input type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[#374151]">End date</span>
              <input type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
          {(m.createBatch.isError || m.updateBatch.isError) && (
            <p className="text-sm text-red-600">Couldn&apos;t save the batch. Please try again.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>Cancel</Button>
          <Button onClick={save} disabled={!canSave} className="bg-[#ff5a1f] hover:bg-[#e64f18]">
            {pending ? 'Saving…' : mode === 'create' ? 'Create batch' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
